import type { RailAreaDef, RailAreaKey } from '@/config/rail-areas'
import type { MouseEvent, PointerEvent, ReactNode } from 'react'
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAuth as useAuthContext } from '@/components/auth/AuthProvider'
import { ScramblMark } from '@/components/brand/ScramblMark'
import { TooltipShortcutHint } from '@/components/layout/KeyboardShortcutsDialog'
import { getCollectionIcon } from '@/components/shared/custom-fields/CollectionIconPicker'
import { useTheme } from '@/components/theme-provider'
import { getAvatarColor } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { type NavItem } from '@/config/navigation'
import {
  AREA_ITEM_ORDER,
  getRailAreaForNavItem,
  NAVIGATION_STYLE,
  RAIL_AREAS,
  SECTION_TO_AREA,
} from '@/config/rail-areas'
import { useLiveChatUnreadCount } from '@/hooks/queries/useLiveChatUnreadCount'
import { useUnreadThreadsCount } from '@/hooks/queries/useThreadsQuery'
import { useUnreadTicketsCount } from '@/hooks/queries/useTicketUnreadCount'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccountType } from '@/hooks/useAccountType'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useFloatingNavExpansion } from '@/hooks/useFloatingNavExpansion'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { usePermissions } from '@/hooks/usePermissions'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { useSidebarPinCapacity } from '@/hooks/useSidebarPinCapacity'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { customCollectionsApi } from '@/lib/api/custom-collections.api'
import { captureRequestScope } from '@/lib/api/request-scope'
import { createLogger } from '@/lib/logger'
import {
  isRecordNavHref,
  normalizeRecordNavHref,
} from '@/lib/navigation/recordNavHref'
import { isSidebarItemHidden } from '@/lib/navigation/sidebar-presets'
import { prefetchModuleForPath } from '@/lib/prefetch/modulePrefetch'
import { useThreadListRealtime } from '@/lib/realtime/features/useThreadListRealtime'
import { useUserPreferencesRealtime } from '@/lib/realtime/features/useUserPreferencesRealtime'
import { cn } from '@/lib/utils'
import { isPopupWindow, isTauri, openFocusWindow } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { getInitials } from '@/utils/getInitials'
import { getOrgPath } from '@/utils/org-paths'
import {
  ArrowSquareOutIcon,
  CaretRightIcon,
  DotsThreeIcon,
  EyeIcon,
  EyeSlashIcon,
  GearSixIcon,
  PushPinIcon,
  PushPinSlashIcon,
  SidebarSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import { AnimatePresence, m } from 'framer-motion'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { FeatureFlagKeyType, PinnedNavRouteItem } from '@scrambl/shared'
import {
  getRequiredSubscriptionPlan,
  MAX_SIDEBAR_PINS,
  PreferenceContextEnum,
  ThreadTypeEnum,
} from '@scrambl/shared'

import { FloatingNavModeMenu } from './FloatingNavModeMenu'
import { Header } from './Header'
import { SidebarNavGroup } from './SidebarNavGroup'

// Stable empty reference so the org-keyed module-pin selector doesn't return a
// fresh array (and re-render) on every store read.
const EMPTY_PINNED_MODULES: string[] = []

// Motion for the rail islands and the hover flyout, on the app's iOS curves
// (index.css MOTION TOKENS): entrances fast-out/long-settle, exits shorter
// and ease-in. Transform + opacity only, so it stays compositor-bound.
const EASE_OUT: [number, number, number, number] = [0.32, 0.72, 0, 1]
const EASE_IN: [number, number, number, number] = [0.4, 0, 1, 1]
const FLYOUT_ENTER = { duration: 0.2, ease: EASE_OUT }
const FLYOUT_EXIT = { duration: 0.13, ease: EASE_IN }
const ROW_ENTER = { duration: 0.2, ease: EASE_OUT }
/** Per-row stagger inside the flyout; capped so long menus don't crawl. */
const ROW_STAGGER = 0.022
const ROW_STAGGER_CAP = 12
const ISLAND_ENTER = { duration: 0.32, ease: EASE_OUT }
const ISLAND_STAGGER = 0.06
const PIN_ENTER = { duration: 0.2, ease: EASE_OUT }
const PIN_EXIT = { duration: 0.13, ease: EASE_IN }
/** Expanded floating rail: an area's rows unfold in place under its row. */
const GROUP_OPEN = { duration: 0.24, ease: EASE_OUT }
const GROUP_CLOSE = { duration: 0.16, ease: EASE_IN }

const logger = createLogger('components/layout/AppSidebar')

/**
 * Pinned avatar components keyed by their visual (href, logo, color, label,
 * color mode). Module scope, not a ref, so render stays pure for the React
 * Compiler; pins are capped at a handful, so the map stays tiny.
 */
const pinnedVisualIconCache = new Map<string, NavItem['icon'] | null>()

interface AppSidebarProps {
  children: ReactNode
  /** Right-hand pane beside the page canvas (e.g. the live recording panel).
   * Rendered inside the frame so it shares the ground the desktop title bar
   * shows through. */
  aside?: ReactNode
}

/**
 * A row in the Pinned section. Records ("route" pins, e.g. a specific client)
 * and modules are pinned through different stores, so each row carries the
 * kind that owns its unpin action.
 */
type PinnedRow = {
  item: NavItem
  kind: 'record' | 'module'
  /** Record pins draw the record's logo or initials as an avatar. */
  isAvatar: boolean
}

// Optimized sidebar trigger with reduced animation
function CustomSidebarTrigger({ className }: { className?: string }) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(state => state.setSidebarOpen)
  const isMobile = useIsMobile()

  // Floating style: the trigger expands or collapses the rail islands
  // (labels beside the icons). It lives in the top bar — the web header
  // here, the desktop title bar's window island in TauriTitleBar.
  if (!isMobile && NAVIGATION_STYLE === 'floating') {
    return <FloatingNavModeMenu className={cn('h-8 w-8', className)} />
  }

  // In rail and floating builds the desktop navigation is a fixed hover
  // rail with no expanded state to toggle, so the trigger only renders on
  // mobile (opens the sheet) and in admin mode. Home Base builds keep it: it
  // toggles between the full sidebar and the compact icon rail.
  if (!isMobile && NAVIGATION_STYLE !== 'homeBase') {
    return null
  }

  const handleToggle = () => {
    toggleSidebar()
    // Direct state update without timeout
    setSidebarOpen(state === 'collapsed')
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          data-sidebar="trigger"
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', className)}
          onClick={handleToggle}
        >
          <SidebarSimpleIcon weight="regular" />
          <span className="sr-only">Toggle Sidebar</span>
        </Button>
      </TooltipTrigger>
      {/* ⌘\ / Ctrl+\ is wired in SidebarProvider (components/ui/sidebar.tsx). */}
      <TooltipContent side="right">
        Toggle sidebar
        <TooltipShortcutHint keys={['Mod', '\\']} />
      </TooltipContent>
    </Tooltip>
  )
}

export function AppSidebar({ children, aside }: AppSidebarProps) {
  const location = useLocation()
  const { user } = useAuthContext()
  const { state, setOpenMobile } = useSidebar()
  const { checkPermission, isLoadingPermissions } = usePermissions()
  const collapsedNavGroups = useUIPreferencesStore(
    state => state.collapsedNavGroups,
  )
  const setNavGroupCollapsed = useUIPreferencesStore(
    state => state.setNavGroupCollapsed,
  )
  const pinnedModulesByOrg = useUIPreferencesStore(
    state => state.pinnedModulesByOrg,
  )
  const pinModule = useUIPreferencesStore(state => state.pinModule)
  const unpinModule = useUIPreferencesStore(state => state.unpinModule)
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme)
  const tauriGlassEnabled = useUIPreferencesStore(
    state => state.tauriGlassEnabled,
  )
  const floatingNavMode = useUIPreferencesStore(state => state.floatingNavMode)
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  // Compute admin status directly from organization to ensure reactivity
  const membershipRole = currentOrganization?.userMembership?.role
  const isExternalAccess = currentOrganization?.isExternalAccess === true
  const preloadProfileImages = useUserProfileStore(
    state => state.preloadProfileImages,
  )
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  // Rail area the user explicitly selected (mobile panel only — desktop uses
  // the hover flyout). Cleared on navigation so the route-derived area takes
  // over again (the panel follows where you are).
  const [selectedArea, setSelectedArea] = useState<RailAreaKey | null>(null)
  // "More" overflow expansion, tracked per rail area (session only).
  const [expandedMoreAreas, setExpandedMoreAreas] = useState<Set<string>>(
    () => new Set(),
  )
  // Optimistic per-user visibility overrides keyed by href: what the click
  // just chose, before (or if) the preference save lands, so the row moves
  // under "More" immediately.
  const visibilityScope = JSON.stringify([
    user?.id,
    currentOrganization?.id,
    currentOrganization?.isExternalAccess,
  ])
  const [pendingVisibility, setPendingVisibility] = useState<{
    scope: string
    items: Record<string, boolean>
  }>({ scope: visibilityScope, items: {} })
  const pendingNavVisibility =
    pendingVisibility.scope === visibilityScope ? pendingVisibility.items : {}
  const visibilityOperations = useRef(new Map<string, symbol>())
  useLayoutEffect(() => {
    // Forget pending choices even for A -> B -> A before the old save settles.
    visibilityOperations.current.clear()
    setPendingVisibility({ scope: visibilityScope, items: {} })
  }, [visibilityScope])
  // Desktop hover flyout: which area's menu is currently flown out. A short
  // close delay bridges the pointer moving between the rail and the flyout.
  const [hoverArea, setHoverArea] = useState<RailAreaKey | null>(null)
  const hoverCloseTimer = useRef<number | null>(null)
  // A row's "…" dropdown renders in a portal outside the flyout, so pointer
  // travel into it looks like leaving the flyout. While any row menu is
  // open, hold the flyout open.
  const rowMenuOpenRef = useRef(false)
  const { theme } = useTheme()
  const organizationId = currentOrganization?.id || null
  const { checkFeature, isLoading: isLoadingSubscriptionFeatures } =
    useSubscriptionFeatures()

  // Get pinned collections for sidebar (must be before isLinkActive which uses it)
  const {
    preferences: navPreferences,
    unpinNavItem,
    isNavRoutePinned,
    setNavItemHidden,
  } = useNavigationPreferences()

  // Navigation preferences can change outside this tab — another device, or a
  // server-side write such as Benny pinning a record to the sidebar. The
  // store's initial fetch runs once, so re-read this one row on the event.
  const syncPreferenceFromServer = useUserPreferencesStore(
    state => state.syncPreferenceFromServer,
  )
  useUserPreferencesRealtime({
    context: PreferenceContextEnum.NAVIGATION,
    onPreferencesUpdated: () => {
      void syncPreferenceFromServer(PreferenceContextEnum.NAVIGATION)
    },
  })

  // Module pins are a per-user, per-org client-side preference (localStorage
  // via the UI preferences store) — no server round-trip, unlike record pins.
  const pinnedModuleHrefs = organizationId
    ? (pinnedModulesByOrg[organizationId] ?? EMPTY_PINNED_MODULES)
    : EMPTY_PINNED_MODULES

  // Older builds pinned modules through the server-side nav preference, so a
  // route pin on a module's own href counts as pinned too.
  const isModulePinned = (href: string) =>
    pinnedModuleHrefs.includes(href) || isNavRoutePinned(href)
  // Pins are capped (MAX_SIDEBAR_PINS): the Pin actions disable at the cap
  // and the user unpins something to make room.
  const pinCapacity = useSidebarPinCapacity()

  const handlePinModule = (href: string) => {
    if (!organizationId) return
    if (pinCapacity.isFull) {
      toast.info(
        `Your sidebar holds ${pinCapacity.max} pins. Unpin one to add more.`,
      )
      return
    }
    pinModule(organizationId, href)
  }

  const handleUnpinModule = (href: string) => {
    if (organizationId) {
      unpinModule(organizationId, href)
    }
    // Also clear any legacy route pin on the same href, otherwise the row
    // would linger in Pinned as a record pin.
    if (isNavRoutePinned(href)) {
      void unpinNavItem(href)
    }
  }

  const hasCollectionsFeature = checkFeature('collections')
  const pinnedCollectionAccessQueries = useQueries({
    queries: (navPreferences.pinnedCollections ?? []).map(collection => ({
      queryKey: [
        'custom-collections',
        'sidebar-access',
        organizationId,
        collection.collectionId,
      ],
      queryFn: () =>
        customCollectionsApi.getCollection(collection.collectionId),
      enabled: !!organizationId && hasCollectionsFeature,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  })
  const accessibleCollectionIds = new Set(
    pinnedCollectionAccessQueries
      .filter(query => query.isSuccess)
      .map(query => query.data.id),
  )
  const accessiblePinnedCollections = (() => {
    if (!navPreferences.pinnedCollections?.length) return []
    return navPreferences.pinnedCollections.filter(item =>
      accessibleCollectionIds.has(item.collectionId),
    )
  })()

  useThreadListRealtime(organizationId, { suppressLogging: true })

  // Light or dark as actually painted: the `dark`/`light` class on <html>,
  // which useThemePreferences sets from the member's synced preference. The
  // ThemeProvider's `theme` reads a different (device) storage key and can
  // disagree with it — e.g. the OS in dark mode while the member chose
  // light — which flipped every light theme's rail to white icons on a
  // light island. Same hook as the title bar, so the two always agree.
  const isDarkMode = useResolvedDarkMode(theme)

  // Get the current theme configuration
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]

  // In dark mode, all themes use light text (dark backgrounds)
  // In light mode, use the theme's specified light text color
  const useDarkText = isDarkMode ? false : themeConfig.lightTextColor === 'dark'

  const isSidebarCollapsed = state === 'collapsed'
  // Home Base style: full-width sidebar with pinned items + collapsible
  // groups (desktop only — the mobile sheet keeps its rail + panel). Set
  // system-wide via NAVIGATION_STYLE in config/rail-areas.ts. When the user
  // collapses the sidebar, Home Base degrades to the icon rail — sub items
  // stay reachable through the hover popovers instead of cramming the tree
  // into icon width.
  const isHomeBase =
    NAVIGATION_STYLE === 'homeBase' && !isMobile && !isSidebarCollapsed
  const isRailMode = !isMobile && !isHomeBase
  // Floating style: the rail's areas, logo and utilities render as detached
  // glass islands over a theme-tinted ground and the page sits in a floating
  // canvas card. Same rail behavior (hover flyouts, area routing, gating) —
  // only the presentation changes, so admin mode and mobile are unaffected.
  const isFloatingNav = isRailMode && NAVIGATION_STYLE === 'floating'
  // The floating shell: tinted ground, canvas card, transparent sidebar
  // column (the rail on desktop; the mobile sheet is unaffected).
  const isFloatingShell = !isMobile && NAVIGATION_STYLE === 'floating'
  const isHybridNav = isFloatingNav && floatingNavMode === 'hybrid'
  const floatingExpansion = useFloatingNavExpansion(
    isFloatingNav ? floatingNavMode : 'closed',
    () => setHoverArea(null),
  )
  const isFloatingExpanded = isFloatingNav && floatingExpansion.isExpanded
  const showRailTooltips = !isFloatingExpanded && !isHybridNav
  // Islands and rows keep ONE geometry at both rail widths: a row is always
  // a full-width flex row with its glyph in a fixed slot 4px from the left,
  // so while the column animates its width the icon never moves or resizes
  // and the label simply comes into view beside it. (Swapping a centered
  // circle for a row, or animating size with framer `layout`, is what used
  // to skew and stretch the icons mid-transition.)
  const railIslandClass =
    'floating-nav-island flex w-full shrink-0 flex-col items-stretch gap-0.5 p-1'
  // Buttons are 36px with the 20px glyph centred, 8px of padding on every
  // side (the title bar's horizontal padding), inset 4px in the 44px island
  // and 2px apart. 18px corners make them circles on the rail and pills
  // when it widens, concentric with the island's 22px radius.
  const railRowClass = cn(
    'relative flex h-9 w-full shrink-0 items-center gap-2.5 px-2 text-left',
    'transition-[background-color,color,box-shadow] duration-300 ease-out motion-reduce:transition-none',
    'rounded-[18px]',
  )
  const railLabelClass = 'min-w-0 flex-1 truncate text-[13px] font-[450]'

  // Floating style: the flyout is a content-sized popover beside the hovered
  // rail icon rather than a full-height sheet. The icon's top (relative to
  // the frame) is captured on hover; the layout effect right below clamps the
  // panel inside the frame once its height is known.
  const frameRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const captureSidebarSurface = useCallback((node: HTMLDivElement | null) => {
    // Sidebar forwards its ref to a wrapper containing a layout spacer.
    // Track the surface receiving pointer events, so entering that spacer
    // cannot cancel closure without a corresponding leave event.
    sidebarRef.current =
      node?.querySelector<HTMLDivElement>('.app-shell-sidebar') ?? node
  }, [])
  const flyoutRef = useRef<HTMLDivElement>(null)
  const [flyoutAnchorTop, setFlyoutAnchorTop] = useState(0)
  const [flyoutTop, setFlyoutTop] = useState(0)

  // Keep the floating flyout inside the frame: top-aligned with the hovered
  // icon, pushed up when its content would run past the bottom gutter, and
  // never above the desktop title bar. Runs before paint so no frame shows
  // the unclamped position.
  useLayoutEffect(() => {
    if (!isFloatingNav || !hoverArea) return
    const frame = frameRef.current
    const panel = flyoutRef.current
    if (!frame || !panel) return
    const gutter = 12
    const shellOffset =
      parseFloat(
        getComputedStyle(frame).getPropertyValue('--tauri-shell-offset'),
      ) || 0
    // Under the desktop title bar the rail starts flush with the shell
    // offset, so only the islands' own inset separates it from the bar.
    const minTop = shellOffset + (shellOffset > 0 ? 6 : gutter)
    const maxTop = frame.clientHeight - gutter - panel.offsetHeight
    setFlyoutTop(Math.max(minTop, Math.min(flyoutAnchorTop, maxTop)))
  }, [isFloatingNav, isFloatingExpanded, hoverArea, flyoutAnchorTop])

  // Simplified profile image loading - use preload method
  useEffect(() => {
    if (user?.id) {
      const timer = setTimeout(() => {
        preloadProfileImages([user.id])
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [user?.id, preloadProfileImages])

  // The panel follows navigation: once the route changes, drop any explicit
  // rail selection so the route-derived area drives the panel again.
  useEffect(() => {
    setSelectedArea(null)
  }, [location.pathname])

  const cancelFlyoutClose = () => {
    if (hoverCloseTimer.current !== null) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
  }

  const openFlyout = (areaKey: RailAreaKey, anchor?: HTMLElement | null) => {
    if (isMobile) return
    floatingExpansion.cancelPendingClose()
    cancelFlyoutClose()
    if (anchor && frameRef.current) {
      setFlyoutAnchorTop(
        anchor.getBoundingClientRect().top -
          frameRef.current.getBoundingClientRect().top,
      )
    }
    setHoverArea(areaKey)
  }

  const scheduleFlyoutClose = () => {
    if (isMobile) return
    if (rowMenuOpenRef.current) return
    cancelFlyoutClose()
    hoverCloseTimer.current = window.setTimeout(() => {
      setHoverArea(null)
      hoverCloseTimer.current = null
    }, 180)
  }

  const handleNavigationPointerEnter = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return
    floatingExpansion.handlePointerEnter()
    cancelFlyoutClose()
  }

  const handleNavigationPointerLeave = (event: PointerEvent<HTMLElement>) => {
    const next = event.relatedTarget
    // The submenu lives beside the sidebar in the DOM. Treat both surfaces
    // as one hover region so crossing into a submenu never collapses it.
    if (
      next instanceof Node &&
      (sidebarRef.current?.contains(next) || flyoutRef.current?.contains(next))
    )
      return
    if (rowMenuOpenRef.current) return
    cancelFlyoutClose()
    floatingExpansion.handlePointerLeave()
  }

  const handleRowMenuOpenChange = (open: boolean) => {
    rowMenuOpenRef.current = open
    cancelFlyoutClose()
    floatingExpansion.cancelPendingClose()
    if (
      !open &&
      !sidebarRef.current?.matches(':hover') &&
      !flyoutRef.current?.matches(':hover')
    ) {
      floatingExpansion.handlePointerLeave()
    }
  }

  // Clear any pending close timer on unmount
  useEffect(() => cancelFlyoutClose, [])

  const handleNavigate = () => {
    // A pointer click keeps the expanded rail and any flyout open; leaving
    // the navigation closes them (handleNavigationPointerLeave). Keyboard
    // activation has no pointer to leave, so it closes them right away.
    const isPointerOverNavigation =
      !isMobile &&
      Boolean(
        sidebarRef.current?.matches(':hover') ||
        flyoutRef.current?.matches(':hover'),
      )
    if (isPointerOverNavigation) return
    cancelFlyoutClose()
    floatingExpansion.collapse()
    setHoverArea(null)
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handleCloseMobile = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  // Memoized link active state
  const isLinkActive = useCallback(
    (path: string) => {
      if (path.includes('/admin/')) {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}${path}` ||
            location.pathname.endsWith(path)
          )
        }
        return location.pathname.endsWith(path)
      }

      if (path === '/help') {
        return (
          location.pathname === '/help' ||
          location.pathname.startsWith('/help/')
        )
      }

      if (path === '/today') {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}/today` ||
            location.pathname === `/${currentOrganization.slug}`
          )
        }
        return location.pathname === '/today'
      }

      // My Work and Workspace Tasks are separate navigation contexts even
      // though they share the underlying task module and route tree.
      if (path === '/tasks/mine') {
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, path)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === path || location.pathname.startsWith(`${path}/`)
        )
      }

      if (path === '/tasks') {
        const orgPath = currentOrganization?.slug
          ? getOrgPath(currentOrganization.slug, path)
          : path
        const minePath = `${orgPath}/mine`
        return (
          (location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)) &&
          location.pathname !== minePath &&
          !location.pathname.startsWith(`${minePath}/`)
        )
      }

      // Notes still uses personal/workspace lenses inside one shared module.
      if (path === '/notes/mine') {
        const modulePath = '/notes'
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, modulePath)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === modulePath ||
          location.pathname.startsWith(`${modulePath}/`)
        )
      }

      // For the generic /collections path, don't highlight if we're on a pinned collection
      if (path === '/collections') {
        const pinnedSlugs = accessiblePinnedCollections.map(c => c.slug)
        const currentPathSegments = location.pathname.split('/')
        // Check if current path is /org/collections/slug where slug is a pinned collection
        if (
          currentPathSegments.length >= 4 &&
          currentPathSegments[2] === 'collections'
        ) {
          const currentSlug = currentPathSegments[3]
          if (pinnedSlugs.includes(currentSlug)) {
            return false // Don't highlight Collections when on a pinned collection page
          }
        }
      }

      if (currentOrganization?.slug) {
        const orgPath = getOrgPath(currentOrganization.slug, path)
        return (
          location.pathname === orgPath ||
          location.pathname.startsWith(`${orgPath}/`)
        )
      }

      return (
        location.pathname === path ||
        (path !== '/' && location.pathname.startsWith(path))
      )
    },
    [location.pathname, currentOrganization, accessiblePinnedCollections],
  )

  // Memoized navigation path
  const getNavigationPath = useCallback(
    (path: string) => {
      const globalRoutes = ['/organizations', '/profile', '/account', '/help']

      // Check exact matches for global routes
      if (globalRoutes.includes(path)) {
        return path
      }

      // User settings routes are global (not org-specific), except integrations which are org-scoped
      if (
        path.startsWith('/settings') &&
        !path.startsWith('/settings/integrations')
      ) {
        return path
      }

      if (path.startsWith('/admin/')) {
        if (currentOrganization?.slug) {
          return `/${currentOrganization.slug}${path}`
        }
        return path
      }

      if (currentOrganization?.slug) {
        return getOrgPath(currentOrganization.slug, path)
      }

      if (path === '/today') {
        return '/today'
      }

      return path
    },
    [currentOrganization],
  )

  // Get navigation items
  const { mainNavItems } = useNavigationItems()
  const featureKeys = useMemo(
    () =>
      mainNavItems
        .map(item => item.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [mainNavItems],
  )
  const { checkFeatureFlag, isLoading: isLoadingFeatureFlags } =
    useFeatureFlags(featureKeys)
  const { accountType, isPersonal } = useAccountType()

  // Flag-enabled modules remain discoverable; show their required plan when
  // the current subscription does not include them.
  const isSubscriptionNavItemLocked = useCallback(
    (item: NavItem) => {
      if (!item.subscriptionFeature || isLoadingSubscriptionFeatures) {
        return false
      }
      return !checkFeature(item.subscriptionFeature)
    },
    [checkFeature, isLoadingSubscriptionFeatures],
  )

  // Determine if we should show loading skeleton for navigation
  // Show skeleton when:
  // 1. Organization exists but userMembership hasn't loaded yet (and it's not external access), OR
  // 2. User is not admin/owner AND permissions are still loading, OR
  // 3. The org's feature flags / subscription features haven't resolved yet.
  //    Both `checkFeatureFlag` and `checkFeature` answer "no" while their
  //    queries are pending, so every gated item is filtered out of
  //    filteredNavItems — without this the sidebar renders an empty
  //    "Overview + WORKSPACE" shell instead of the skeleton on a cold boot.
  // Note: membershipRole and isExternalAccess are already defined at the top of the component
  const isAdminOrOwner =
    membershipRole === 'OWNER' || membershipRole === 'ADMIN'
  // For external org access, we don't have userMembership - permissions come from the external org relationship
  // So we skip the userMembership check for external access
  const isNavLoading = Boolean(
    (currentOrganization?.id &&
      !currentOrganization?.userMembership?.id &&
      !isExternalAccess) ||
    (!isAdminOrOwner && !isExternalAccess && isLoadingPermissions) ||
    (currentOrganization?.id &&
      (isLoadingFeatureFlags || isLoadingSubscriptionFeatures)),
  )

  // Get unread threads count for badge
  const { data: unreadThreadsCount } = useUnreadThreadsCount({
    threadType: ThreadTypeEnum.DISCUSSION,
  })

  // Unread tickets count for the Tickets nav badge
  const { data: unreadTicketsCount } = useUnreadTicketsCount()
  const { data: unreadLiveChatCount } = useLiveChatUnreadCount({
    enabled: checkFeatureFlag('LIVE_CHAT'),
  })

  // Focus windows are available from the desktop row context menu.
  const canOpenFocusWindow = isTauri() && !isPopupWindow() && !isMobile
  const isDesktopTauri = isTauri() && !isMobile

  // Handle opening a nav item in a focus window
  const handleOpenFocusWindow = useCallback(
    async (path: string, label: string) => {
      const fullPath = getNavigationPath(path)
      await openFocusWindow(fullPath, label)
    },
    [getNavigationPath],
  )

  // Warm the route's JS chunk on hover intent. Pure dynamic import — no API
  // traffic — so it's safe on web too, not just desktop.
  const handlePrefetch = (path: string) => {
    if (isPopupWindow()) return
    void prefetchModuleForPath(path)
  }

  // Memoized filtered navigation items
  // Persisted hide choices with any just-clicked overrides layered on top.
  // A member's own choice wins; otherwise modules on a higher plan than the
  // org's start under "More" so the upsell rows don't crowd the main list.
  const effectiveHiddenNavItems: Record<string, boolean> = {
    ...(navPreferences.hiddenNavItems ?? {}),
    ...pendingNavVisibility,
  }
  const isNavItemHidden = (item: NavItem) =>
    isSidebarItemHidden(item.href, {
      preset:
        !isPersonal && !isExternalAccess
          ? navPreferences.sidebarPreset
          : 'default',
      override: effectiveHiddenNavItems[item.href],
      subscriptionLocked: isSubscriptionNavItemLocked(item),
    })

  const handleToggleNavVisibility = (href: string, nextHidden: boolean) => {
    const request = captureRequestScope()
    const operation = Symbol(href)
    visibilityOperations.current.set(href, operation)
    setPendingVisibility(previous => ({
      scope: visibilityScope,
      items: {
        ...(previous.scope === visibilityScope ? previous.items : {}),
        [href]: nextHidden,
      },
    }))
    void setNavItemHidden(href, nextHidden)
      .catch((error: unknown) => {
        if (request.signal.aborted) return
        logger.error('Failed to save nav visibility', error)
        toast.error('Navigation changes couldn’t be saved. Please try again.')
      })
      .finally(() => {
        if (visibilityOperations.current.get(href) !== operation) return
        visibilityOperations.current.delete(href)
        setPendingVisibility(previous => {
          if (previous.scope !== visibilityScope) return previous
          const items = { ...previous.items }
          delete items[href]
          return { ...previous, items }
        })
      })
  }

  const filteredNavItems = (() => {
    return mainNavItems.filter(item => {
      // Account-type gate for surfaces feature flags can't express. Absent =
      // visible to all account types.
      if (item.accountTypes && !item.accountTypes.includes(accountType)) {
        return false
      }

      // Feature flag is the org-level toggle and matches what the route guard checks.
      // If an admin explicitly enables the flag, the route is already reachable, so the
      // sidebar should show the item too — even if the subscription plan technically
      // doesn't include it. Enforce the subscription gate only when no feature flag is
      // configured on the nav item, so plan-only items still respect their tier.
      if (item.featureFlag) {
        if (!checkFeatureFlag(item.featureFlag)) {
          return false
        }
      } else if (
        item.subscriptionFeature &&
        !checkFeature(item.subscriptionFeature)
      ) {
        return false
      }

      if (
        item.permission &&
        !checkPermission(item.permission.module, item.permission.action)
      ) {
        return false
      }

      return true
    })
  })()

  // Group the (already fully gated) nav items by rail area. Pinned collections
  // join the area their section maps to and render as regular nav items.
  const areaNavItems = (() => {
    const map = new Map<RailAreaKey, NavItem[]>()
    const push = (area: RailAreaKey, item: NavItem) => {
      const list = map.get(area)
      if (list) {
        list.push(item)
      } else {
        map.set(area, [item])
      }
    }
    filteredNavItems.forEach(item => {
      push(getRailAreaForNavItem(item.href, item.section, { isPersonal }), item)
    })
    // Areas mixing items from multiple sections get an explicit read order.
    map.forEach((items, areaKey) => {
      const order = AREA_ITEM_ORDER[areaKey]
      if (order) {
        items.sort((a, b) => {
          const rank = (href: string) => {
            const index = order.indexOf(href)
            return index === -1 ? order.length : index
          }
          return rank(a.href) - rank(b.href)
        })
      }
    })
    accessiblePinnedCollections.forEach(pinned => {
      push(pinned.section ? SECTION_TO_AREA[pinned.section] : 'work', {
        href: `/collections/${pinned.slug}`,
        label: pinned.name,
        icon: getCollectionIcon(pinned.icon),
      })
    })
    return map
  })()

  // Areas render on the rail only when they have at least one item the user
  // can see — so flag/permission/subscription gating shapes the rail too.
  const visibleAreas = RAIL_AREAS.filter(
    area => (areaNavItems.get(area.key)?.length ?? 0) > 0,
  )

  // Roll the per-item unread counts up to the rail icons.
  const unreadByHref: Record<string, number | undefined> = {
    '/threads': unreadThreadsCount,
    '/tickets': unreadTicketsCount,
    '/live-chat': unreadLiveChatCount,
  }
  const areaUnreadCounts = (() => {
    const counts: Partial<Record<RailAreaKey, number>> = {}
    areaNavItems.forEach((items, areaKey) => {
      const total = items.reduce(
        (sum, item) => sum + (unreadByHref[item.href] ?? 0),
        0,
      )
      if (total > 0) {
        counts[areaKey] = total
      }
    })
    return counts
  })()

  // Which area the current route belongs to (drives the panel by default).
  const routeArea = (() => {
    for (const item of filteredNavItems) {
      if (isLinkActive(item.href)) {
        return getRailAreaForNavItem(item.href, item.section, { isPersonal })
      }
    }
    for (const pinned of accessiblePinnedCollections) {
      if (isLinkActive(`/collections/${pinned.slug}`)) {
        return pinned.section ? SECTION_TO_AREA[pinned.section] : 'work'
      }
    }
    return null
  })()

  const activeAreaKey = selectedArea ?? routeArea ?? 'home'
  const activeArea =
    visibleAreas.find(area => area.key === activeAreaKey) ?? visibleAreas[0]
  // The flown-out area on desktop (hover-driven).
  const hoverAreaDef = hoverArea
    ? visibleAreas.find(area => area.key === hoverArea)
    : undefined

  // Personal accounts retitle some areas ("People & Home").
  const getAreaLabel = useCallback(
    (area: RailAreaDef) => (isPersonal && area.personalLabel) || area.label,
    [isPersonal],
  )

  // Rail click on the icon-only rail. Desktop: the flyout is already open on
  // hover, so a click jumps straight to the area's first visible item.
  // Mobile: switch the panel. (The expanded floating rail toggles the area's
  // inline group instead — see renderRailArea.)
  const handleSelectArea = (areaKey: RailAreaKey) => {
    if (isMobile) {
      setSelectedArea(areaKey)
      return
    }
    const items = areaNavItems.get(areaKey) ?? []
    const target = items.find(item => !isNavItemHidden(item)) ?? items[0]
    if (target) {
      handleNavigate()
      navigate(getNavigationPath(target.href))
    }
  }

  // Dynamic text color classes based on theme
  const textClasses = useDarkText
    ? 'text-black/80 hover:text-black'
    : 'text-white/80 hover:text-white'
  const textMutedClasses = useDarkText ? 'text-black/60' : 'text-white/60'
  // Section labels use the .section-header utility for typography; its
  // muted-foreground color and bottom margin are overridden (via important
  // modifiers, since the utility compiles after core utilities) so the
  // theme-aware sidebar text colors still apply.
  const sectionLabelColorClasses = useDarkText
    ? '!text-black/60'
    : '!text-white/60'
  const hoverBgClasses = useDarkText ? 'hover:bg-black/10' : 'hover:bg-white/10'
  const activeBgClasses = useDarkText
    ? 'data-[active=true]:bg-black/15 data-[active=true]:text-black'
    : 'data-[active=true]:bg-white/15 data-[active=true]:text-white'
  // Selected rail rows have to read on every sidebar theme, from pale
  // Sunny Yolk to near-black Cast Iron. The fill is derived from the
  // island's own text colour, so contrast holds by construction. It is a
  // light, flat wash so the island shows through; the accent ring carries
  // the selection.
  const railActiveClasses = useDarkText
    ? 'bg-black/[0.08] text-black'
    : 'bg-white/[0.12] text-white'
  // The selected ring is the sidebar theme's accent (--icon-accent, set per
  // theme in colors.css), the same for every area, so the selection matches
  // the theme rather than each area's own colour. A 1px hairline: a heavier
  // ring crowds the 4px island inset. Rows without an area accent keep the
  // plain tonal selection.
  const railActiveAccentStyle = (accent?: string) =>
    accent ? { boxShadow: 'inset 0 0 0 1px var(--icon-accent)' } : undefined
  /** Selected rows carry a little more weight than their neighbours. */
  const railActiveLabelClass = 'font-[560]'
  const borderClasses = useDarkText ? 'border-black/10' : 'border-white/5'

  // Shared row renderer for area menus and pinned routes/modules.
  const renderNavItem = (
    item: NavItem,
    options?: {
      pinned?: PinnedRow['kind']
      /** The row lives under its area's "More" toggle. */
      hidden?: boolean
      /**
       * Area accent when the row sits in an unfolded rail group. The selected
       * row then takes over the area row's selected look (fill, ring, radius)
       * so the selection reads as one highlight, not two.
       */
      accent?: string
    },
  ) => {
    const isActive = isLinkActive(item.href)
    const groupAccent = options?.accent
    const pinnedKind = options?.pinned
    const isPinnedRow = pinnedKind !== undefined
    const isItemHidden = options?.hidden === true
    // Pinned rows are managed from the Pinned section; hiding is for area rows.
    const canHideRow = !isPinnedRow
    // Home-area rows are always visible at the top of the sidebar — pinning
    // them would just duplicate them, so the option is omitted.
    const isHomeItem =
      !!item.section &&
      getRailAreaForNavItem(item.href, item.section, { isPersonal }) === 'home'
    const subscriptionFeature = item.subscriptionFeature
    const unreadBadgeCount = unreadByHref[item.href]
    const showUnreadBadge =
      typeof unreadBadgeCount === 'number' && unreadBadgeCount > 0
    const isSubscriptionLocked = isSubscriptionNavItemLocked(item)
    const subscriptionBadge = subscriptionFeature
      ? (getRequiredSubscriptionPlan(subscriptionFeature)?.displayName ?? 'Pro')
      : 'Pro'
    // A workspace row is "pinned" when its module is pinned; a row already in
    // the Pinned section always is. Home rows sit at the top of the sidebar
    // already, so pinning them would only duplicate them.
    const isRowPinned = isPinnedRow || isModulePinned(item.href)
    const canPinRow = !isHomeItem
    const togglePin = () => {
      if (pinnedKind === 'record') {
        void unpinNavItem(item.href)
        return
      }
      if (isRowPinned) {
        handleUnpinModule(item.href)
        return
      }
      handlePinModule(item.href)
    }

    const row = (
      <SidebarMenuItem
        key={item.href}
        className="group/navitem relative flex justify-center"
      >
        <SidebarMenuButton
          asChild
          isActive={isActive}
          size="sm"
          className={cn(
            'h-8 px-2.5 text-left',
            groupAccent
              ? // Inline rail rows sit inside the icon-collapsed Sidebar,
                // whose menu-button rules (!rounded-none, !p-2, centered)
                // are meant for icon-only buttons. Restore the row shape.
                'rounded-xl group-data-[collapsible=icon]:!justify-start group-data-[collapsible=icon]:!rounded-xl group-data-[collapsible=icon]:!px-2.5 group-data-[collapsible=icon]:!py-0'
              : 'rounded-lg',
            textClasses,
            hoverBgClasses,
            groupAccent && isActive
              ? cn(
                  railActiveClasses,
                  useDarkText
                    ? 'data-[active=true]:bg-black/[0.18]'
                    : 'data-[active=true]:bg-white/25',
                )
              : activeBgClasses,
            isMobile && 'h-8 w-full px-4',
          )}
          style={
            groupAccent && isActive
              ? railActiveAccentStyle(groupAccent)
              : undefined
          }
        >
          <Link
            to={getNavigationPath(item.href)}
            aria-current={isActive ? 'page' : undefined}
            className="no-animate relative flex items-center gap-2"
            onClick={handleNavigate}
            onMouseEnter={() => handlePrefetch(item.href)}
            onFocus={() => handlePrefetch(item.href)}
          >
            <div
              aria-hidden
              className="relative flex h-4 w-4 shrink-0 items-center justify-center"
            >
              <item.icon
                className="h-4 w-4"
                weight={isActive ? 'fill' : 'regular'}
              />
            </div>
            <span
              className={cn(
                'flex-1 truncate text-[13px]',
                isActive ? 'font-[550]' : 'font-[450]',
                // Rows under "More" read quieter than the main list.
                isItemHidden && !isActive && 'opacity-60',
              )}
            >
              {item.label}
            </span>
            {isSubscriptionLocked && (
              <span className="rounded-full bg-amber-300/95 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-amber-950 shadow-sm ring-1 ring-black/10">
                {subscriptionBadge}
              </span>
            )}
            {showUnreadBadge && (
              <span
                className={cn(
                  'min-w-[18px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold',
                  'bg-primary text-primary-foreground',
                )}
              >
                {unreadBadgeCount > 99 ? '99+' : unreadBadgeCount}
              </span>
            )}
          </Link>
        </SidebarMenuButton>
        {/* Keep the pin button outside the link as its own keyboard target. */}
        <div className="absolute right-0.5 top-1/2 flex -translate-y-1/2 items-center">
          {canHideRow && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleToggleNavVisibility(item.href, !isItemHidden)
              }}
              aria-label={
                isItemHidden ? `Show ${item.label}` : `Hide ${item.label}`
              }
              className={cn(
                'bg-sidebar focus-visible:ring-sidebar-ring grid h-6 w-6 place-items-center rounded-md p-0 opacity-0 transition-opacity',
                'hover:bg-white/20 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1',
                'group-hover/navitem:opacity-100',
              )}
            >
              {isItemHidden ? (
                <EyeIcon className="h-3.5 w-3.5" />
              ) : (
                <EyeSlashIcon className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          {canPinRow && (
            // No tooltip: the button only appears on row hover, so its
            // tooltip fired on top of the row label it sits against. The
            // aria-label still names the action for screen readers.
            <button
              type="button"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                togglePin()
              }}
              aria-label={
                isRowPinned ? `Unpin ${item.label}` : `Pin ${item.label}`
              }
              className={cn(
                'bg-sidebar focus-visible:ring-sidebar-ring grid h-6 w-6 place-items-center rounded-md p-0 opacity-0 transition-opacity',
                'hover:bg-white/20 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1',
                'group-hover/navitem:opacity-100',
              )}
            >
              {isRowPinned ? (
                <PushPinSlashIcon className="h-3.5 w-3.5" />
              ) : (
                <PushPinIcon className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </SidebarMenuItem>
    )

    if (!canOpenFocusWindow) return row

    return (
      <ContextMenu key={item.href} onOpenChange={handleRowMenuOpenChange}>
        <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onSelect={() => void handleOpenFocusWindow(item.href, item.label)}
          >
            <ArrowSquareOutIcon className="mr-2 h-3.5 w-3.5" />
            Open in window
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    )
  }

  // Area menu: the rows the member keeps, then a "More" toggle holding the
  // ones they hid. Hiding is a per-user preference; gating stays upstream.
  const renderAreaPanel = (
    area: RailAreaDef,
    options?: {
      /** Flyout rows slide in one after another when the menu opens. */
      staggerIn?: boolean
      /** Rows sit in an unfolded rail group (see renderNavItem's accent). */
      accent?: string
    },
  ) => {
    const combinedItems = areaNavItems.get(area.key) ?? []
    const accent = options?.accent
    const staggerIn = options?.staggerIn === true
    const enterRow = (node: ReactNode, index: number, key: string) =>
      staggerIn ? (
        <m.div
          key={key}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            ...ROW_ENTER,
            delay: Math.min(index, ROW_STAGGER_CAP) * ROW_STAGGER,
          }}
        >
          {node}
        </m.div>
      ) : (
        node
      )

    const visibleItems = combinedItems.filter(item => !isNavItemHidden(item))
    const hiddenItems = combinedItems.filter(item => isNavItemHidden(item))
    const isMoreExpanded = expandedMoreAreas.has(area.key)
    // The current page stays visible even when hidden, so the sidebar always
    // marks where you are — except right after the hide click, when the row
    // moves under "More" at once as feedback.
    const activeHiddenItems = hiddenItems.filter(
      item =>
        isLinkActive(item.href) && pendingNavVisibility[item.href] !== true,
    )
    const toggleMore = () =>
      setExpandedMoreAreas(prev => {
        const next = new Set(prev)
        if (next.has(area.key)) next.delete(area.key)
        else next.add(area.key)
        return next
      })

    return (
      <div className="w-full max-w-full space-y-0.5 overflow-hidden">
        {visibleItems.map((item, index) =>
          enterRow(renderNavItem(item, { accent }), index, item.href),
        )}
        {!isMoreExpanded &&
          activeHiddenItems.map((item, index) =>
            enterRow(
              renderNavItem(item, { hidden: true, accent }),
              visibleItems.length + index,
              item.href,
            ),
          )}
        {isMoreExpanded &&
          hiddenItems.map(item =>
            renderNavItem(item, { hidden: true, accent }),
          )}
        {hiddenItems.length > 0 && (
          <SidebarMenuItem className="flex justify-center">
            <SidebarMenuButton
              size="sm"
              aria-expanded={isMoreExpanded}
              className={cn(
                'h-7 rounded-lg px-2.5 text-left opacity-75 transition-opacity hover:opacity-100',
                // Same icon-collapse overrides as the rail group rows.
                accent &&
                  'rounded-xl group-data-[collapsible=icon]:!justify-start group-data-[collapsible=icon]:!rounded-xl group-data-[collapsible=icon]:!px-2.5 group-data-[collapsible=icon]:!py-0',
                textMutedClasses,
                hoverBgClasses,
                isMobile && 'w-full px-4',
              )}
              onClick={toggleMore}
            >
              <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                <DotsThreeIcon className="h-3.5 w-3.5" weight="bold" />
              </div>
              <span className="flex-1 text-xs font-[450]">
                {isMoreExpanded ? 'Less' : 'More'}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {/* Pinned shortcuts live under Home while the sidebar is collapsed to
            the icon rail (and in the mobile panel) — the expanded Home Base
            sidebar renders its own Pinned section instead, and the floating
            rail shows them as icons in its Home island. */}
        {area.key === 'home' &&
          !isHomeBase &&
          !isFloatingNav &&
          pinnedRows.length > 0 && (
            <div className="pt-3">
              <div
                className={cn(
                  'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
                  sectionLabelColorClasses,
                )}
              >
                Pinned
              </div>
              <div className="space-y-0.5">
                {pinnedRows.map(row =>
                  renderNavItem(row.item, { pinned: row.kind }),
                )}
              </div>
            </div>
          )}
      </div>
    )
  }

  // Floating rail rows always render their label, inside a clipped slot that
  // collapses to zero width on the icon rail. It fades in a beat after the
  // rail starts widening and fades out at once when it narrows. The rows
  // carry aria-labels, so the visual label stays out of the a11y tree.
  const renderRailTrailing = (
    label: string,
    isActive: boolean,
    extra?: ReactNode,
  ) =>
    isFloatingNav ? (
      <span
        aria-hidden
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 overflow-hidden transition-opacity ease-out motion-reduce:transition-none',
          isFloatingExpanded
            ? 'opacity-100 delay-100 duration-200'
            : 'opacity-0 duration-100',
        )}
      >
        <span className={cn(railLabelClass, isActive && railActiveLabelClass)}>
          {label}
        </span>
        {extra}
      </span>
    ) : null

  const renderRailUnreadBadge = (count: number | undefined) =>
    typeof count === 'number' && count > 0 ? (
      <span
        aria-hidden
        className={cn(
          'flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-blue-500 px-1 text-[9px] font-bold leading-none text-white ring-1 ring-black/20',
          isFloatingExpanded ? 'ml-auto' : 'absolute -right-1 -top-1',
        )}
      >
        {count > 99 ? '99+' : count}
      </span>
    ) : null

  // One row on the rail per area, with rolled-up unread counts.
  //
  // Icon-only rail: hovering (or focusing) an icon flies the area's menu out
  // beside it; no tooltip needed since the flyout header names the area.
  //
  // Expanded floating rail (Open, or Hybrid while hovered): the row is an
  // inline group. Clicking it unfolds the area's rows underneath, in the
  // island, and the choice persists per area (the same preference the Home
  // Base groups use). An area holding the current page starts open.
  const renderRailArea = (area: RailAreaDef) => {
    const isAreaActive = activeArea?.key === area.key
    const isInlineGroup = isFloatingExpanded
    const isAreaFlownOut = !isInlineGroup && hoverAreaDef?.key === area.key
    const isAreaOpen = isInlineGroup && openRailAreaKey === area.key
    const unreadCount = areaUnreadCounts[area.key]
    const label = getAreaLabel(area)
    // While the area is unfolded its current row carries the selection, so
    // the area row steps back to a filled icon and a bolder label. Folded
    // (or on the icon rail) it keeps the full fill + accent ring.
    const isAreaHighlighted = isAreaActive && !isAreaOpen

    // One stable wrapper in both rail widths, so Hybrid expanding under the
    // pointer never remounts the row it is hovering.
    return (
      <div
        key={area.key}
        className={cn(
          'flex flex-col',
          isInlineGroup ? 'w-full' : 'items-center',
        )}
      >
        <button
          type="button"
          aria-label={label}
          aria-description={unreadCount ? `${unreadCount} unread` : undefined}
          aria-pressed={isAreaActive}
          aria-expanded={isInlineGroup ? isAreaOpen : isAreaFlownOut}
          onClick={() =>
            isInlineGroup
              ? handleToggleRailGroup(area.key, !isAreaOpen)
              : handleSelectArea(area.key)
          }
          onMouseEnter={event => {
            // Hybrid's icon rail expands on hover instead of flying out.
            if (!isInlineGroup && !isHybridNav)
              openFlyout(area.key, event.currentTarget)
          }}
          onFocus={event => {
            if (!isInlineGroup && !isHybridNav)
              openFlyout(area.key, event.currentTarget)
          }}
          className={cn(
            'transition-colors',
            isFloatingNav
              ? railRowClass
              : 'relative grid h-9 w-9 shrink-0 place-items-center rounded-lg',
            isAreaHighlighted
              ? railActiveClasses
              : cn(
                  textClasses,
                  hoverBgClasses,
                  isAreaActive && (useDarkText ? 'text-black' : 'text-white'),
                  isAreaFlownOut &&
                    (useDarkText ? 'bg-black/10' : 'bg-white/10'),
                ),
          )}
          style={
            isAreaHighlighted ? railActiveAccentStyle(area.accent) : undefined
          }
        >
          <span
            aria-hidden
            className="flex h-5 w-5 shrink-0 items-center justify-center"
          >
            <area.icon
              className="h-5 w-5"
              weight={isAreaActive ? 'fill' : 'regular'}
            />
          </span>
          {renderRailTrailing(
            label,
            isAreaActive,
            <>
              {/* Unfolded, the rows underneath carry their own counts. */}
              {isFloatingExpanded &&
                !isAreaOpen &&
                renderRailUnreadBadge(unreadCount)}
              <CaretRightIcon
                aria-hidden
                className={cn(
                  'h-3 w-3 shrink-0 opacity-60 transition-transform duration-200 motion-reduce:transition-none',
                  isAreaOpen && 'rotate-90',
                )}
              />
            </>,
          )}
          {!isFloatingExpanded && renderRailUnreadBadge(unreadCount)}
        </button>
        <AnimatePresence initial={false}>
          {isAreaOpen && (
            <m.div
              key="group"
              // rail-inline-group: theme.css fades it out the moment the rail
              // starts to narrow (the exiting copy keeps its expanded props).
              className="rail-inline-group overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: 'auto',
                opacity: 1,
                transition: GROUP_OPEN,
              }}
              exit={{ height: 0, opacity: 0, transition: GROUP_CLOSE }}
            >
              {/* A hairline drops from the area icon to tie the rows to it;
                  rows are inset so their glyphs sit under the area label. */}
              <div
                className={cn(
                  'relative ml-[1.125rem] pb-1 pl-2.5 pt-1',
                  'before:absolute before:bottom-1.5 before:left-0 before:top-1 before:w-px before:bg-current before:opacity-15',
                )}
              >
                {renderAreaPanel(area, { accent: area.accent })}
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  // Floating rail: one nav item (a Home module or a pin) as a round icon.
  // Click opens it; hovering it lets any open area flyout close, the same as
  // leaving the rail. `accent` tints the active state like the area chips;
  // `onUnpin` adds a right-click menu (record pins can also be unpinned from
  // their page's overflow menu, module pins from their row in the area
  // flyout, so the context menu is a shortcut, not the only way out).
  const renderRailItem = (
    item: NavItem,
    options: { accent?: string; onUnpin?: () => void; avatar?: boolean } = {},
  ) => {
    const isActive = isLinkActive(item.href)
    const unreadCount = unreadByHref[item.href]
    const { accent, onUnpin, avatar } = options
    const link = (
      <Link
        to={getNavigationPath(item.href)}
        aria-current={isActive ? 'page' : undefined}
        aria-label={item.label}
        aria-description={unreadCount ? `${unreadCount} unread` : undefined}
        onClick={handleNavigate}
        onMouseEnter={() => {
          scheduleFlyoutClose()
          handlePrefetch(item.href)
        }}
        onFocus={() => handlePrefetch(item.href)}
        className={cn(
          'no-animate transition-colors',
          railRowClass,
          // A 28px avatar needs 4px insets to share the glyphs' 18px center.
          avatar && 'px-1',
          isActive ? railActiveClasses : cn(textClasses, hoverBgClasses),
        )}
        style={isActive ? railActiveAccentStyle(accent) : undefined}
      >
        {/* Record avatars fill more of the circle than a glyph would. One
            size at both widths so nothing resizes while the rail animates. */}
        <item.icon
          weight={isActive ? 'fill' : 'regular'}
          className={cn('shrink-0', avatar ? 'h-7 w-7 text-[11px]' : 'h-5 w-5')}
        />
        {renderRailTrailing(
          item.label,
          isActive,
          isFloatingExpanded && renderRailUnreadBadge(unreadCount),
        )}
        {!isFloatingExpanded && renderRailUnreadBadge(unreadCount)}
      </Link>
    )
    const trigger = onUnpin ? (
      <ContextMenuTrigger asChild>{link}</ContextMenuTrigger>
    ) : (
      link
    )
    // Expanded rows carry their label; only the icon rail needs a tooltip.
    const tooltip = !showRailTooltips ? (
      <Fragment key={item.href}>{trigger}</Fragment>
    ) : (
      <Tooltip key={item.href}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    )
    if (!onUnpin) return tooltip
    return (
      <ContextMenu key={item.href} onOpenChange={handleRowMenuOpenChange}>
        {tooltip}
        <ContextMenuContent className="min-w-[10rem]">
          <ContextMenuItem className="text-xs" onClick={onUnpin}>
            <PushPinSlashIcon className="mr-2 h-3.5 w-3.5" />
            Unpin
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    )
  }

  const renderRailPinned = (row: PinnedRow) =>
    renderRailItem(row.item, {
      avatar: row.isAvatar,
      onUnpin: () => {
        if (row.kind === 'record') {
          void unpinNavItem(row.item.href)
          return
        }
        handleUnpinModule(row.item.href)
      },
    })

  // Fixed bottom options, rendered twice: icon-only on the rail, and with
  // labels in the flyout's footer while the menu is expanded.
  const utilityItems: {
    key: string
    label: string
    icon: ReactNode
    isActive?: boolean
    onClick?: (e: MouseEvent<HTMLElement>) => void
    href?: string
    prefetchPath?: string
    /** Real shortcut for this item, appended to its rail tooltip. */
    shortcutKeys?: string[]
  }[] = [
    // Help Center and Keyboard shortcuts live in the user menu (Header /
    // TauriTitleBar) — the rail keeps only workspace-level utilities.
    // Settings — the unified org settings hub. Open to every member: org
    // settings they can't manage render read-only. Explicit org path —
    // getNavigationPath treats /settings as the global appearance route.
    ...(currentOrganization?.slug
      ? [
          {
            key: 'settings',
            label: 'Settings',
            icon: <GearSixIcon className="h-4 w-4" />,
            isActive: isLinkActive('/settings'),
            href: `/${currentOrganization.slug}/settings`,
          },
        ]
      : []),
  ]

  // Utility (Settings) icons at the bottom of the rail. Administration
  // pages live inside Settings now (the Settings hub hosts them behind the
  // owner/admin gate), so there is no separate admin mode to enter.
  const renderRailUtility = ({
    key,
    label,
    icon,
    isActive,
    onClick,
    href,
    prefetchPath,
    shortcutKeys,
  }: (typeof utilityItems)[number]) => {
    const sharedClassName = cn(
      'no-animate transition-colors',
      isFloatingNav
        ? railRowClass
        : 'relative grid h-9 w-9 shrink-0 place-items-center rounded-lg',
      textClasses,
      hoverBgClasses,
      isActive && railActiveClasses,
    )
    const content = isFloatingNav ? (
      <>
        {/* Same 20px glyph slot as the other rail rows. */}
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          {icon}
        </span>
        {renderRailTrailing(label, !!isActive)}
      </>
    ) : (
      icon
    )
    const prefetchHandlers = prefetchPath
      ? {
          onMouseEnter: () => handlePrefetch(prefetchPath),
          onFocus: () => handlePrefetch(prefetchPath),
        }
      : {}

    return (
      <Tooltip key={key}>
        <TooltipTrigger asChild>
          {href ? (
            <Link
              to={href}
              aria-current={isActive ? 'page' : undefined}
              aria-label={label}
              onClick={handleNavigate}
              className={sharedClassName}
              {...prefetchHandlers}
            >
              {content}
            </Link>
          ) : (
            <button
              type="button"
              aria-label={label}
              onClick={onClick}
              className={sharedClassName}
              {...prefetchHandlers}
            >
              {content}
            </button>
          )}
        </TooltipTrigger>
        {showRailTooltips && (
          <TooltipContent side="right">
            {label}
            {shortcutKeys && <TooltipShortcutHint keys={shortcutKeys} />}
          </TooltipContent>
        )}
      </Tooltip>
    )
  }

  // Labeled variant of the same options for the flyout footer.
  const renderFlyoutUtility = ({
    key,
    label,
    icon,
    isActive,
    onClick,
    href,
    prefetchPath,
  }: (typeof utilityItems)[number]) => {
    const sharedClassName = cn(
      // no-animate opts out of the global a:hover primary-color effect
      'no-animate flex h-8 w-full items-center gap-2 rounded-lg px-2.5 text-[13px] font-[450] transition-colors',
      textClasses,
      hoverBgClasses,
      isActive && railActiveClasses,
    )
    const prefetchHandlers = prefetchPath
      ? {
          onMouseEnter: () => handlePrefetch(prefetchPath),
          onFocus: () => handlePrefetch(prefetchPath),
        }
      : {}

    return href ? (
      <Link
        key={key}
        to={href}
        aria-current={isActive ? 'page' : undefined}
        onClick={handleNavigate}
        className={sharedClassName}
        {...prefetchHandlers}
      >
        {icon}
        <span className="truncate">{label}</span>
      </Link>
    ) : (
      <button
        key={key}
        type="button"
        onClick={onClick}
        className={sharedClassName}
        {...prefetchHandlers}
      >
        {icon}
        <span className="truncate">{label}</span>
      </button>
    )
  }

  // ---- Home Base style: pinned rows + collapsible area groups ----
  // Pinned = user-pinned page routes (records) plus user-pinned modules. A
  // pinned module is a shortcut, not a move: it keeps rendering inside its
  // workspace group as well. Pinned collections are modules, so they stay
  // inside their module's group (injected via areaNavItems).
  // A pinned record shows as an avatar — its uploaded logo or photo when the
  // pin captured one, otherwise its initials on the record's color (or a
  // stable color hashed from the name, the same way member avatars work).
  // Pinned modules and other pages keep the module's icon; ad-hoc pages fall
  // back to the pin glyph.
  const resolvePinnedIcon = (href: string) =>
    filteredNavItems.find(
      item => item.href === href || href.startsWith(`${item.href}/`),
    )?.icon ?? PushPinIcon
  // A pin on a module's own href is that module: show the sidebar's live
  // label (org terminology renames Clients/Vendors) rather than whatever the
  // label said when the pin was written.
  const resolvePinnedLabel = (pinned: PinnedNavRouteItem) =>
    filteredNavItems.find(item => item.href === pinned.href)?.label ??
    pinned.label
  const createPinnedVisualIcon = (
    pinned: PinnedNavRouteItem,
  ): NavItem['icon'] | null => {
    // Same visual → same component type. A fresh function per render would
    // make React remount the avatar (and reload its logo) on every rail
    // hover or expand, which reads as a flicker mid-animation.
    const cacheKey = [
      pinned.href,
      pinned.logoUrl ?? '',
      pinned.color ?? '',
      pinned.label,
      isDarkMode ? 'dark' : 'light',
    ].join('|')
    const cached = pinnedVisualIconCache.get(cacheKey)
    if (cached !== undefined) return cached
    const icon = buildPinnedVisualIcon(pinned)
    pinnedVisualIconCache.set(cacheKey, icon)
    return icon
  }
  const buildPinnedVisualIcon = (
    pinned: PinnedNavRouteItem,
  ): NavItem['icon'] | null => {
    const { logoUrl, color, label } = pinned
    const isRecord = isRecordNavHref(pinned.href)
    if (logoUrl) {
      return function PinnedLogoIcon({ className }: { className?: string }) {
        return (
          <img
            src={logoUrl}
            alt=""
            className={cn(
              'object-cover',
              isRecord ? 'rounded-full' : 'rounded-mark',
              className,
            )}
          />
        )
      }
    }
    if (!isRecord && !color) return null
    const backgroundColor =
      color ?? getAvatarColor(label, isDarkMode ? 'dark' : 'light')
    const initials = isRecord
      ? getInitials(label) || label.charAt(0).toUpperCase()
      : label.charAt(0).toUpperCase()
    return function PinnedInitialIcon({ className }: { className?: string }) {
      return (
        <span
          className={cn(
            'flex items-center justify-center text-[9px] font-bold leading-none tracking-tight text-white',
            isRecord ? 'rounded-full' : 'rounded-mark',
            className,
          )}
          style={{ backgroundColor }}
        >
          {initials}
        </span>
      )
    }
  }
  // Pinned modules resolve against the nav items the sidebar actually renders
  // (areaNavItems = fully gated modules + accessible pinned collections), so
  // all feature-flag / subscription / permission / account-type gating from
  // useNavigationItems carries straight through. A module the user loses
  // access to silently disappears from Pinned; the stored id is left alone so
  // the pin returns if access does.
  const pinnableNavItems: NavItem[] = Array.from(areaNavItems.values()).flat()
  const pinnedModuleRows: PinnedRow[] = pinnedModuleHrefs
    .map(href => pinnableNavItems.find(item => item.href === href))
    .filter((item): item is NavItem => item !== undefined)
    // Pin order (oldest first) comes from the stored list — no sorting.
    .map(item => ({ item, kind: 'module' as const, isAvatar: false }))
  const pinnedModuleHrefSet = new Set(
    pinnedModuleRows.map(row => row.item.href),
  )

  // Pins stored before record hrefs were normalized point at whichever tab the
  // user pinned from ('/projects/<id>/tasks'). Normalize on read so existing
  // pins open the record itself without anyone having to re-pin.
  const seenPinnedRecordHrefs = new Set<string>()
  const pinnedRecordRows: PinnedRow[] = (navPreferences.pinnedNavItems ?? [])
    .map(pinned => ({ ...pinned, href: normalizeRecordNavHref(pinned.href) }))
    // A module pinned client-side owns its row; drop any legacy route pin on
    // the same href so it doesn't render twice.
    .filter(pinned => !pinnedModuleHrefSet.has(pinned.href))
    // Two legacy pins on different tabs of the same record collapse to one row.
    .filter(pinned => {
      if (seenPinnedRecordHrefs.has(pinned.href)) return false
      seenPinnedRecordHrefs.add(pinned.href)
      return true
    })
    .map(pinned => ({
      item: {
        href: pinned.href,
        label: resolvePinnedLabel(pinned),
        icon: createPinnedVisualIcon(pinned) ?? resolvePinnedIcon(pinned.href),
      },
      isAvatar: isRecordNavHref(pinned.href),
    }))
    // Alphabetical, so the section stays scannable as pins accumulate.
    .sort((a, b) =>
      a.item.label.localeCompare(b.item.label, undefined, {
        sensitivity: 'base',
      }),
    )
    .map(({ item, isAvatar }) => ({ item, kind: 'record' as const, isAvatar }))

  // Records keep their existing behaviour and position; pinned modules slot in
  // after them, in pin order.
  const pinnedRows: PinnedRow[] = [...pinnedRecordRows, ...pinnedModuleRows]

  // Floating rail groups: all available Home modules, the user's pins, then
  // workspace areas. The slice guards pins saved before the cap was introduced.
  const homeRailArea = RAIL_AREAS.find(area => area.key === 'home')
  // Hidden Home modules leave the island; the Home Base/mobile Home list
  // keeps them under "More".
  const homeRailItems = (areaNavItems.get('home') ?? []).filter(
    item => !isNavItemHidden(item),
  )
  const workspaceRailAreas = visibleAreas.filter(area => area.key !== 'home')

  // Expanded rail groups open one at a time. Stored choices are shared with
  // the Home Base groups and may predate this rule, so resolve to a single
  // open area here: the current page's area wins, else the first open one.
  const isRailGroupStoredOpen = (key: RailAreaKey) =>
    !(collapsedNavGroups[key] ?? routeArea !== key)
  const openRailAreaKey = (() => {
    const open = workspaceRailAreas.filter(area =>
      isRailGroupStoredOpen(area.key),
    )
    return (
      open.find(area => area.key === routeArea)?.key ?? open[0]?.key ?? null
    )
  })()

  // Opening an area folds every other one in the same write, so the rail
  // never shows two unfolded groups, even for a frame.
  const handleToggleRailGroup = (areaKey: RailAreaKey, open: boolean) => {
    const updates: Record<string, boolean> = { [areaKey]: !open }
    if (open) {
      for (const area of workspaceRailAreas) {
        if (area.key !== areaKey) updates[area.key] = true
      }
    }
    useUIPreferencesStore.setState(state => ({
      collapsedNavGroups: { ...state.collapsedNavGroups, ...updates },
    }))
  }
  const railPinnedRows = pinnedRows.slice(0, MAX_SIDEBAR_PINS)

  const renderHomeBaseNav = () => {
    const homeArea = visibleAreas.find(area => area.key === 'home')
    const groupAreas = visibleAreas.filter(area => area.key !== 'home')

    return (
      <div>
        {homeArea && renderAreaPanel(homeArea)}

        {pinnedRows.length > 0 && (
          <div className="pt-4">
            <div
              className={cn(
                'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
                sectionLabelColorClasses,
              )}
            >
              Pinned
            </div>
            <div className="space-y-0.5">
              {pinnedRows.map(row =>
                renderNavItem(row.item, { pinned: row.kind }),
              )}
            </div>
          </div>
        )}

        <div className="pt-4">
          <div
            className={cn(
              'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
              sectionLabelColorClasses,
            )}
          >
            Workspace
          </div>
          <div className="space-y-0.5">
            {groupAreas.map(area => (
              <SidebarNavGroup
                key={area.key}
                label={getAreaLabel(area)}
                icon={area.icon}
                containsActive={routeArea === area.key}
                storedCollapsed={collapsedNavGroups[area.key]}
                unreadCount={areaUnreadCounts[area.key]}
                className={cn(textClasses, hoverBgClasses, activeBgClasses)}
                onCollapsedChange={collapsed =>
                  setNavGroupCollapsed(area.key, collapsed)
                }
              >
                {renderAreaPanel(area)}
              </SidebarNavGroup>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={frameRef}
      className="app-shell-frame relative flex h-full min-w-0 flex-1"
      data-sidebar-collapsed={isSidebarCollapsed ? 'true' : 'false'}
      // Rail mode's footprint is the rail alone (see theme.css) — the area
      // menu flies out over the content on hover instead of occupying layout.
      // Admin mode and the Home Base style keep the full-width sidebar.
      data-rail-only={isRailMode ? 'true' : 'false'}
      // Floating style hooks (theme.css): wider rail column for the island
      // gutters, tinted ground, and lighter island glass when the desktop
      // app's native glass already shows through the sidebar.
      data-nav-style={isFloatingShell ? 'floating' : 'docked'}
      data-nav-expanded={isFloatingExpanded ? 'true' : 'false'}
      data-nav-mode={isFloatingNav ? floatingNavMode : undefined}
      // Which text tone the sidebar theme uses in this mode (theme.css keys
      // the selected-icon paint off it: white body + dark outline on light
      // themes, solid on dark ones).
      data-nav-text={useDarkText ? 'dark' : 'light'}
      data-glass={isDesktopTauri && tauriGlassEnabled ? 'true' : 'false'}
    >
      <Sidebar
        ref={captureSidebarSurface}
        onPointerEnter={handleNavigationPointerEnter}
        onPointerLeave={handleNavigationPointerLeave}
        variant="sidebar"
        collapsible={useIsMobile() ? 'offcanvas' : 'icon'}
        tauriOffset={isTauri() && !isMobile}
        className={cn(
          'themed-sidebar enhanced-sidebar app-shell-sidebar bg-sidebar transition-colors duration-300',
          isDesktopTauri && 'tauri-desktop-sidebar',
          isDesktopTauri && tauriGlassEnabled && 'tauri-glass-sidebar',
          isFloatingShell && 'floating-nav-sidebar',
          isMobile
            ? '!sm:p-0 !md:p-0 border-r-0 !p-0'
            : isDesktopTauri || isFloatingShell
              ? 'border-r-0'
              : 'border-r',
          borderClasses,
        )}
      >
        {/* Header section - Logo for web, close button for mobile */}
        {isMobile ? (
          <div className="bg-sidebar flex h-14 items-center justify-between px-4 transition-colors duration-300">
            <Link
              to="/organizations"
              className="hover:opacity-80"
              onClick={handleNavigate}
            >
              <span
                className={cn(
                  'font-kronaone text-lg font-normal tracking-wide',
                  useDarkText ? 'text-black/85' : 'text-white/85',
                )}
              >
                scrambl
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseMobile}
              className={cn('h-8 w-8', textClasses, hoverBgClasses)}
            >
              <XIcon className="h-5 w-5" weight="regular" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
        ) : (
          !isTauri() && (
            <div
              className={cn(
                isFloatingShell
                  ? // The shell's 0.375rem gutter, like the rail islands below.
                    cn('flex px-1.5 pt-1.5', !isFloatingNav && 'justify-center')
                  : 'bg-sidebar flex h-14 items-center justify-center px-3 transition-colors duration-300',
              )}
            >
              <Link
                to="/organizations"
                className={cn(
                  'hover:opacity-80',
                  // Floating: the logo sits on its own above the islands —
                  // the mark alone at rail width, the full lockup once the
                  // rail is wide enough to carry the wordmark.
                  // The mark keeps a fixed 48px slot so it holds still while
                  // the rail widens; the wordmark fades in, clipped, beside.
                  // 40px tall, like the header islands it lines up with.
                  isFloatingShell &&
                    'flex h-10 w-full min-w-0 items-center overflow-hidden',
                )}
                onClick={handleNavigate}
              >
                {/* Rail-only footprint fits the mark, not the wordmark; the
                    wide admin-mode and Home Base sidebars show the wordmark. */}
                {isFloatingShell ? (
                  <>
                    {/* The full-color mark, drawn inline and flat. It fills
                        more of the 48px slot than the old 24px image: the mark
                        is detail-dense (tapering ribbons that cross), so at
                        24px a third of its pixels were partial rather than
                        solid, against 15% at 32px. Size is the only lever that
                        sharpens it without a keyline (sub-pixel, so it smears)
                        or tone-shifting the brand fills (muddies the orange). */}
                    {/* 48px wide: the rail island's width, so the mark stays
                        centered over the rail icons. Collapsed it stands
                        alone (24px); expanded it sizes down to the
                        wordmark beside it (20px). */}
                    <span className="grid h-10 w-11 shrink-0 place-items-center">
                      <ScramblMark
                        className={cn(
                          'transition-[width,height] duration-200 ease-out motion-reduce:transition-none',
                          isFloatingExpanded ? 'h-5 w-5' : 'h-6 w-6',
                        )}
                      />
                    </span>
                    {/* The wordmark sits on the tinted ground, not on an
                        island, so it takes the page's text color rather
                        than the sidebar theme's (which is written for the
                        island fill and can be light on a pale ground). */}
                    <span
                      aria-hidden
                      className={cn(
                        // -ml-1: starts in line with the rail rows' labels.
                        'floating-nav-wordmark font-kronaone -ml-1 whitespace-nowrap text-base font-normal tracking-wide transition-opacity ease-out motion-reduce:transition-none',
                        isFloatingExpanded
                          ? 'opacity-100 delay-100 duration-200'
                          : 'opacity-0 duration-100',
                      )}
                    >
                      scrambl
                    </span>
                  </>
                ) : isRailMode || isSidebarCollapsed ? (
                  <img
                    src="/logo-outline.svg"
                    alt="Scrambl"
                    className="h-8 w-8"
                    style={{
                      filter: useDarkText
                        ? 'invert(1) opacity(0.8)'
                        : 'opacity(0.8)',
                    }}
                  />
                ) : (
                  <span
                    className={cn(
                      'font-kronaone text-lg font-normal tracking-wide',
                      useDarkText ? 'text-black/85' : 'text-white/85',
                    )}
                  >
                    scrambl
                  </span>
                )}
              </Link>
            </div>
          )
        )}

        <div className="relative flex min-h-0 flex-1">
          {/* Icon rail — one entry per area with visible modules. Hidden in
              admin mode and Home Base style, where the panel takes the full
              width. */}
          {!isHomeBase && (
            <div
              onPointerEnter={cancelFlyoutClose}
              onPointerLeave={event => {
                // Leaving all navigation is handled by the shared grace
                // period. Only close the flyout here for moves within the
                // sidebar (for example, from the rail into its header).
                if (
                  event.relatedTarget instanceof Node &&
                  sidebarRef.current?.contains(event.relatedTarget)
                ) {
                  scheduleFlyoutClose()
                }
              }}
              className={cn(
                isFloatingShell
                  ? // Floating: islands stack in the column with the shell's
                    // 0.375rem gutter. Web starts level with the header's
                    // islands; desktop starts under the title bar.
                    cn(
                      'flex w-full flex-none flex-col items-center gap-3 px-1.5 pb-1.5',
                      !isDesktopTauri && 'pt-1.5',
                    )
                  : 'flex w-12 flex-none flex-col items-center gap-1 py-2',
                !isFloatingShell && 'bg-sidebar transition-colors duration-300',
                // Mobile keeps the static panel next to the rail; give the
                // pair a divider. On desktop the rail is the whole sidebar.
                isMobile && cn('border-r', borderClasses),
              )}
            >
              {/* The islands scroll; the utility island below (Settings) stays
                  pinned to the bottom of the rail however long the open
                  groups run. */}
              <div
                className={cn(
                  'sidebar-scrollbar flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto',
                  isFloatingShell ? 'gap-3' : 'gap-1',
                )}
              >
                {isNavLoading ? (
                  <div
                    className={cn(
                      'flex flex-col items-center',
                      isFloatingNav
                        ? 'floating-nav-island shrink-0 gap-0.5 p-1'
                        : 'contents',
                    )}
                  >
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton
                        key={i}
                        className={cn(
                          'h-9 w-9 shrink-0',
                          isFloatingNav ? 'rounded-full' : 'rounded-lg',
                          useDarkText ? 'bg-black/10' : 'bg-white/10',
                        )}
                      />
                    ))}
                  </div>
                ) : isFloatingNav ? (
                  <>
                    {/* Home island: the Home modules as icons. */}
                    {homeRailItems.length > 0 && (
                      <m.div
                        className={railIslandClass}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={ISLAND_ENTER}
                      >
                        {homeRailItems.map(item =>
                          renderRailItem(item, {
                            accent: homeRailArea?.accent,
                          }),
                        )}
                      </m.div>
                    )}
                    {/* Pins island: appears with the first pin and leaves with
                      the last. Nothing here uses transforms that resize —
                      no framer `layout`, no scale — because the rail's width
                      animation would project them onto the avatars and
                      stretch them. Pins reveal by height + opacity instead. */}
                    <AnimatePresence initial={false}>
                      {railPinnedRows.length > 0 && (
                        <m.div
                          key="pins"
                          className={railIslandClass}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            transition: {
                              ...ISLAND_ENTER,
                              delay: ISLAND_STAGGER,
                            },
                          }}
                          exit={{
                            opacity: 0,
                            y: -6,
                            transition: PIN_EXIT,
                          }}
                        >
                          <AnimatePresence initial={false}>
                            {railPinnedRows.map(row => (
                              <m.div
                                key={row.item.href}
                                // Height + opacity only: the row opens its own
                                // space (siblings glide with it) while the
                                // avatar keeps its true size throughout.
                                className="overflow-hidden"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{
                                  opacity: 1,
                                  height: 'auto',
                                  transition: PIN_ENTER,
                                }}
                                exit={{
                                  opacity: 0,
                                  height: 0,
                                  transition: PIN_EXIT,
                                }}
                              >
                                {renderRailPinned(row)}
                              </m.div>
                            ))}
                          </AnimatePresence>
                        </m.div>
                      )}
                    </AnimatePresence>
                    {workspaceRailAreas.length > 0 && (
                      <m.div
                        className={railIslandClass}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          ...ISLAND_ENTER,
                          delay: ISLAND_STAGGER * 2,
                        }}
                      >
                        {workspaceRailAreas.map(renderRailArea)}
                      </m.div>
                    )}
                  </>
                ) : (
                  visibleAreas.map(renderRailArea)
                )}
              </div>
              {/* Docked rail: while the flyout is open its labeled footer
                  stands in for these — hide the icon set so the options
                  don't double up. Floating keeps its utility island put (the
                  flyout carries no footer) so nothing jumps on hover. */}
              {utilityItems.length > 0 &&
                (isFloatingNav ? (
                  <m.div
                    className={railIslandClass}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      ...ISLAND_ENTER,
                      delay: ISLAND_STAGGER * 2,
                    }}
                  >
                    {utilityItems.map(renderRailUtility)}
                  </m.div>
                ) : (
                  !hoverAreaDef && utilityItems.map(renderRailUtility)
                ))}
            </div>
          )}

          {(isMobile || isHomeBase) && (
            <SidebarContent
              className={cn(
                'bg-sidebar min-h-0 min-w-0 flex-1 transition-colors duration-300',
                isMobile ? '!p-0' : '',
              )}
            >
              <div className="sidebar-scrollbar h-full overflow-y-auto">
                <SidebarMenu
                  className={cn(
                    'space-y-1',
                    // Adjust padding for mobile vs desktop
                    isMobile
                      ? 'px-3 pb-6 pt-2'
                      : 'pb-4 pt-1 group-data-[state=collapsed]:px-0 group-data-[state=expanded]:px-2',
                  )}
                >
                  {/* Navigation */}
                  {isNavLoading ? (
                    // Loading skeleton for the panel
                    <div className="space-y-4">
                      <div>
                        <div className="mb-2 px-1">
                          <Skeleton
                            className={cn(
                              'h-4 w-16 rounded',
                              useDarkText ? 'bg-black/10' : 'bg-white/10',
                            )}
                          />
                        </div>
                        <div className="space-y-1">
                          {[1, 2, 3, 4, 5].map(i => (
                            <div
                              key={i}
                              className="flex h-8 items-center gap-2 px-3"
                            >
                              <Skeleton
                                className={cn(
                                  'h-5 w-5 rounded',
                                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                                )}
                              />
                              <Skeleton
                                className={cn(
                                  'h-4 w-24 rounded',
                                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : isHomeBase ? (
                    renderHomeBaseNav()
                  ) : (
                    activeArea && (
                      <div>
                        {/* Panel header: the active area's name with its accent */}
                        <div className="mb-2 flex items-center gap-2 px-2.5 pt-1">
                          <span
                            aria-hidden
                            className="h-2 w-2 shrink-0 rounded-[3px]"
                            style={{ backgroundColor: activeArea.accent }}
                          />
                          <h2
                            className={cn(
                              'section-header !mb-0',
                              sectionLabelColorClasses,
                            )}
                          >
                            {getAreaLabel(activeArea)}
                          </h2>
                        </div>
                        {renderAreaPanel(activeArea)}
                      </div>
                    )
                  )}
                </SidebarMenu>
              </div>
            </SidebarContent>
          )}
        </div>

        {/* Home Base footer: the fixed bottom options with labels */}
        {isHomeBase && (
          <div className={cn('space-y-0.5 border-t px-2 py-2', borderClasses)}>
            {utilityItems.map(renderFlyoutUtility)}
          </div>
        )}
      </Sidebar>
      {/* Desktop hover flyout: the hovered area's menu floats next to the
          rail, above the content, and hides when the pointer leaves the
          rail + flyout pair. Rendered at the frame level (not inside the
          Sidebar) because the sidebar container clips overflowing children. */}
      <AnimatePresence>
        {isRailMode && hoverAreaDef && (
          <m.div
            key="nav-flyout"
            onPointerEnter={handleNavigationPointerEnter}
            onPointerLeave={handleNavigationPointerLeave}
            ref={flyoutRef}
            className={cn(
              'themed-sidebar absolute z-50 flex w-56 flex-col overflow-hidden',
              isFloatingNav
                ? // Floating: a content-sized glass popover beside the
                  // hovered icon (top set by the clamp effect above).
                  'floating-nav-island floating-nav-flyout left-[3.25rem]'
                : 'bottom-0 left-12 shadow-2xl',
            )}
            style={
              isFloatingNav
                ? {
                    transformOrigin: 'left 1.25rem',
                    top: flyoutTop,
                    maxHeight: `calc(100% - ${flyoutTop}px - 0.75rem)`,
                  }
                : {
                    transformOrigin: 'left center',
                    top: isDesktopTauri ? 'var(--tauri-shell-offset, 52px)' : 0,
                  }
            }
            // The panel slides and settles in, and slips back out when the
            // pointer leaves; AnimatePresence keeps the last area's content
            // on screen for the exit.
            initial={{ opacity: 0, x: -12, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1, transition: FLYOUT_ENTER }}
            exit={{ opacity: 0, x: -8, scale: 0.98, transition: FLYOUT_EXIT }}
          >
            <div className="sidebar-scrollbar min-h-0 flex-1 overflow-y-auto">
              {/* Keyed by area so switching areas replays the row entrance. */}
              <SidebarMenu
                key={hoverAreaDef.key}
                className="space-y-1 px-2 pb-4 pt-2"
              >
                <div className="mb-2 flex items-center gap-2 px-2.5 pt-1">
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-[3px]"
                    style={{
                      backgroundColor: hoverAreaDef.accent,
                    }}
                  />
                  <h2
                    className={cn(
                      'section-header !mb-0',
                      sectionLabelColorClasses,
                    )}
                  >
                    {getAreaLabel(hoverAreaDef)}
                  </h2>
                </div>
                {renderAreaPanel(hoverAreaDef, { staggerIn: true })}
              </SidebarMenu>
            </div>
            {/* Labeled bottom options, aligned with the rail's fixed icons.
              Floating keeps the rail's utility island visible instead. */}
            {!isFloatingNav && (
              <div
                className={cn('space-y-0.5 border-t px-2 py-2', borderClasses)}
              >
                {utilityItems.map(renderFlyoutUtility)}
              </div>
            )}
          </m.div>
        )}
      </AnimatePresence>
      <SidebarInset
        className={cn(
          'app-shell-inset flex h-full min-h-0 w-full min-w-0 flex-col',
          isFloatingShell
            ? // Floating: the inset is just the gutter; the canvas card below
              // paints the page surface. Top gutter comes from the canvas
              // margin on web and the title bar's own padding on desktop.
              'bg-transparent pb-1.5 pr-1.5'
            : isDesktopTauri
              ? tauriGlassEnabled
                ? 'bg-transparent'
                : 'bg-background'
              : 'bg-background',
          !isMobile && !isFloatingShell && 'mb-4 ml-0',
          !isMobile && !isDesktopTauri && !isFloatingShell && 'shadow-sm',
        )}
        tauriOffset={isTauri() && !isMobile}
      >
        {/* Floating on web: the header's islands sit on the ground above the
            canvas, like the desktop title bar; its padding is the canvas's
            top gutter. (Desktop renders its title bar at the layout level.) */}
        {isFloatingShell && !isDesktopTauri && <Header variant="floating" />}
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            isFloatingShell && 'floating-nav-canvas overflow-hidden',
          )}
        >
          {!isFloatingShell && <Header />}
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            {isDesktopTauri && !isFloatingShell && (
              <div
                aria-hidden
                className={cn(
                  'app-shell-corner-notch pointer-events-none absolute left-0 top-0 z-0 h-4 w-4',
                  tauriGlassEnabled ? 'tauri-glass-corner-fill' : 'bg-sidebar',
                )}
              />
            )}
            <div
              className={cn(
                'app-shell-content-surface relative z-10 h-full min-h-0 min-w-0 overflow-hidden',
                // The canvas card clips its own corners in floating mode.
                isFloatingShell ? 'rounded-none' : 'rounded-tl-2xl',
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </SidebarInset>
      {aside}
    </div>
  )
}

export { CustomSidebarTrigger }
